import { useEffect, useState } from 'react'
import { getNodeConfigState } from '@/api/nodes'
import type { NodeConfigStateResponse } from '@/types'
import { formatKstDateTime } from '@/utils/datetime'

interface Props {
  selectedNodeId: string
}

function formatTime(ts: string | null | undefined): string {
  return formatKstDateTime(ts, '—')
}

export default function NodeConfigStatus({ selectedNodeId }: Props) {
  const [state, setState] = useState<NodeConfigStateResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedNodeId) {
      setState(null)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    getNodeConfigState(selectedNodeId)
      .then((res) => {
        setState(res)
      })
      .catch((e: unknown) => {
        const err = e as { message?: string }
        setError(err?.message ?? 'Failed to load config state')
        setState(null)
      })
      .finally(() => setLoading(false))
  }, [selectedNodeId])

  if (!selectedNodeId) {
    return (
      <div style={cardStyle}>
        <div style={cardTitleStyle}>Node Config State</div>
        <p style={{ fontSize: 13, color: '#aaa', margin: 0 }}>Select a node to view config state.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={cardTitleStyle}>Node Config State</div>
        <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={cardStyle}>
        <div style={cardTitleStyle}>Node Config State</div>
        <div style={{ fontSize: 13, color: '#c62828' }}>{error}</div>
      </div>
    )
  }

  if (!state) return null

  // Drift: desired != applied (or applied is null while desired is set)
  const hasDrift =
    state.desired_revision != null &&
    state.applied_revision !== state.desired_revision

  const syncStatus = state.error_message
    ? 'error'
    : hasDrift
    ? 'drift'
    : 'synced'

  const statusColor =
    syncStatus === 'error'
      ? '#c62828'
      : syncStatus === 'drift'
      ? '#e65100'
      : '#2e7d32'

  const statusLabel =
    syncStatus === 'error'
      ? 'Error'
      : syncStatus === 'drift'
      ? 'Drift'
      : 'Synced'

  return (
    <div style={cardStyle}>
      <div style={cardTitleStyle}>Node Config State</div>
      <div style={{ fontSize: 13, color: '#444' }}>
        <Row label="Sync status">
          <span style={{ color: statusColor, fontWeight: 700 }}>{statusLabel}</span>
        </Row>
        <Row label="Desired rev">
          {state.desired_revision != null ? String(state.desired_revision) : '—'}
        </Row>
        <Row label="Applied rev">
          <span style={{ color: hasDrift ? '#e65100' : undefined, fontWeight: hasDrift ? 700 : undefined }}>
            {state.applied_revision != null ? String(state.applied_revision) : '—'}
          </span>
        </Row>
        <Row label="Last fetched">{formatTime(state.last_fetched_at)}</Row>
        <Row label="Last applied">{formatTime(state.last_applied_at)}</Row>
        {state.error_message && (
          <Row label="Error">
            <span style={{ color: '#c62828', fontSize: 12 }}>{state.error_message}</span>
          </Row>
        )}
      </div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
      <span style={{ color: '#888', minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span>{children}</span>
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  padding: '14px 16px',
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: '#333',
  marginBottom: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}
